import { useState } from 'react'
import { useNotificationPreferences, useSetNotificationPreferences } from '../api/rooms'
import type { NotificationPreferences } from '../api/types'
import { errorMessage } from '../lib/format'

/**
 * The caller's own email settings for this room, behind one button in the room header.
 * The digest is opt-in; Q&A, the request list and the access reminder are on unless turned off. Saved on
 * every change; the server answers with what it stored.
 */
export function EmailPreferences({ roomId }: { roomId: string }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="email-prefs">
      <button
        type="button"
        className="btn"
        aria-expanded={open}
        aria-controls="email-prefs-panel"
        onClick={() => setOpen((o) => !o)}
      >
        Email alerts
      </button>
      {open && (
        <div id="email-prefs-panel" className="panel email-prefs-panel">
          <EmailPreferencesForm roomId={roomId} />
        </div>
      )}
    </div>
  )
}

export function EmailPreferencesForm({ roomId }: { roomId: string }) {
  const prefs = useNotificationPreferences(roomId)
  const save = useSetNotificationPreferences(roomId)
  if (prefs.isPending) return <p className="muted small">Loading…</p>
  if (prefs.isError) return <p className="error-text small">{errorMessage(prefs.error)}</p>
  const p = prefs.data
  const set = (patch: Partial<NotificationPreferences>) =>
    save.mutate({
      digestEnabled: p.digestEnabled,
      qaEnabled: p.qaEnabled,
      expiryEnabled: p.expiryEnabled,
      ...patch,
    })
  return (
    <fieldset className="stack-sm" disabled={save.isPending}>
      <legend className="small muted">Email me about this room</legend>
      <label className="check">
        <input
          type="checkbox"
          checked={p.digestEnabled}
          onChange={(e) => set({ digestEnabled: e.target.checked })}
        />{' '}
        New documents (a daily digest of what I can see)
      </label>
      <label className="check">
        <input
          type="checkbox"
          checked={p.qaEnabled}
          onChange={(e) => set({ qaEnabled: e.target.checked })}
        />{' '}
        Q&amp;A activity I can see
      </label>
      <label className="check">
        <input
          type="checkbox"
          checked={p.expiryEnabled}
          onChange={(e) => set({ expiryEnabled: e.target.checked })}
        />{' '}
        A reminder before my access ends
      </label>
      {/* Shown only when the API knows of it: one rolled back to before ADR 0009 sends no such field, and a
          switch that did nothing would be worse than none. The other three toggles never send it, so the
          API leaves it as it is. */}
      {p.checklistEnabled !== undefined && (
        <label className="check">
          <input
            type="checkbox"
            checked={p.checklistEnabled}
            onChange={(e) => set({ checklistEnabled: e.target.checked })}
          />{' '}
          The request list: what is assigned to me, sent back or submitted, and goal-date reminders
        </label>
      )}
      {save.isError && <p className="error-text small">{errorMessage(save.error)}</p>}
    </fieldset>
  )
}
